import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import * as R from 'ramda';
import { Avatar, Box, CircularProgress, Divider, Typography, useTheme } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import { ProductIndicatorBadge } from 'common';
import { totalProgress } from 'feature/loading-management/utils/calculate';
import { useSnackbar } from 'notistack';
import IndividualProgressIndicator from '../../../create-new-common/IndividualProgressIndicator';

const avatarClassStyles = theme => ({
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.dark,
  fontSize: '16px',
  height: theme.spacing(3),
  marginRight: theme.spacing(1.5),
  width: theme.spacing(3),
});

const checkboxIconStyles = theme => ({
  color: theme.palette.graphic.main,
});

const headerStyles = theme => ({
  color: theme.palette.primary.dark,
  margin: theme.spacing('6px', 0, 0, 2),
  padding: theme.spacing(1.5, 0),
});

const productStyles = theme => ({
  alignItems: 'center',
  borderBottom: `solid 1px ${theme.palette.graphic.light}`,
  display: 'grid',
  gridAutoColumns: 'min-content 222px min-content min-content',
  gridAutoFlow: 'column',
  gridGap: theme.spacing(2),
  padding: theme.spacing(2),
});

const progressIconStyles = theme => ({
  marginRight: theme.spacing(1.5),
});

const ProgressOptions = ({ progress }) => {
  const theme = useTheme();
  const boxStyles = {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    height: '74px',
    justifyContent: 'flex-start',
    margin: '16px 24px 24px',
    mx: 'auto',
    pl: 6,
  };

  return (
    <Box {...boxStyles} bgcolor="surface.white">
      {progress !== 100 ? (
        <>
          <CircularProgress color="secondary" size={24} sx={progressIconStyles(theme)} />
          <Typography color="inherit" variant="h6">
            Total Progress. . . {progress}%
          </Typography>
        </>
      ) : (
        <>
          <Avatar sx={avatarClassStyles(theme)}>
            <CheckRoundedIcon />
          </Avatar>
          <Typography color="inherit" variant="h6">
            All Setups Complete!
          </Typography>
        </>
      )}
    </Box>
  );
};

ProgressOptions.propTypes = {
  progress: number.isRequired,
};

const Progress = props => {
  const {
    authorizationBy,
    createSupplierAuthorization,
    location,
    params,
    product,
    refreshSupplierAuthorizationsListView,
    selectedProducts,
    setSubmittingBatch,
    supplier,
  } = props;

  const {
    authorizationNumber,
    authorizationType,
    authorizedVolume,
    endDate,
    renewalVolume,
    supplierIdName,
  } = params;

  const isMounted = useRef(true);
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [productProgress, setProductProgress] = useState([]);

  const total = totalProgress(productProgress, selectedProducts);

  useEffect(() => {
    const apiParams = {
      authorizationNumber,
      authorizationType,
      authorizedVolume,
      endDate,
      renewalVolume,
      supplierIdName,
    };

    setSubmittingBatch(true);

    // prevent updating state when unmounted
    const updateState = result => {
      if (isMounted.current) {
        setProductProgress(R.append(result));
        if (result.error && result.error?.status) {
          const message =
            typeof result.error?.title === 'string' && result.error.title?.trim()
              ? result.error.title
              : null;
          enqueueSnackbar(message, { variant: 'error' });
        }
      }
    };

    R.forEach(
      R.compose(
        R.andThen(updateState),
        createSupplierAuthorization,
        R.mergeRight(apiParams),
        R.pick(['inventoryOwnerSelfAuthorizationId', 'value'])
      ),
      selectedProducts
    );

    return () => {
      isMounted.current = false;
    };
  }, [
    authorizationNumber,
    enqueueSnackbar,
    authorizationType,
    authorizedVolume,
    createSupplierAuthorization,
    endDate,
    renewalVolume,
    selectedProducts,
    setProductProgress,
    setSubmittingBatch,
    supplierIdName,
  ]);

  useEffect(() => {
    if (total === 100) {
      setSubmittingBatch(false);
      refreshSupplierAuthorizationsListView();
    }
  }, [refreshSupplierAuthorizationsListView, setSubmittingBatch, total]);

  return (
    <>
      <Box bgcolor="graphic.lighter" color="primary.dark" pb={3} pt={2} px={2} width="100%">
        <Typography color="inherit" sx={headerStyles(theme)} variant="body1">
          Setting Up Supplier Authorization for {supplier.nameShort}{' '}
          {authorizationBy === 'location' ? `at ${location.nameShort}` : `for ${product.nameShort}`}
        </Typography>
        <ProgressOptions progress={total} />
      </Box>

      <Divider />

      {R.map(({ label, proprietary, value }) => {
        const item = productProgress.find(({ productId }) => productId === value);

        return (
          <Box key={value} sx={productStyles(theme)}>
            <CheckBoxRoundedIcon color="inherit" sx={checkboxIconStyles(theme)} />
            <Typography color="textPrimary" sx={{ fontWeight: 'bold' }} variant="body1">
              {label}
            </Typography>
            <IndividualProgressIndicator
              error={item?.error ?? ''}
              isSuccessful={item?.success ?? false}
            />
            <ProductIndicatorBadge proprietary={proprietary} />
          </Box>
        );
      }, selectedProducts)}
    </>
  );
};

Progress.propTypes = {
  authorizationBy: string.isRequired,
  createSupplierAuthorization: func.isRequired,
  location: shape({
    nameShort: string,
  }),
  params: shape({
    authorizationNumber: string,
    authorizationType: string,
    authorizedVolume: number,
    endDate: string,
    renewalVolume: number,
    supplierIdName: string,
  }).isRequired,
  product: shape({
    nameShort: string,
  }),
  refreshSupplierAuthorizationsListView: func.isRequired,
  selectedProducts: arrayOf(shape).isRequired,
  setSubmittingBatch: func.isRequired,
  supplier: shape({
    nameShort: string,
  }).isRequired,
};

Progress.defaultProps = {
  location: null,
  product: null,
};

export default Progress;
